/* eslint-disable max-len */
/* eslint-disable max-statements */
/* eslint-disable complexity */
import {makeObservable, observable, action, flow, computed} from 'mobx';
import {v4 as uuidv4} from 'uuid';
import {App} from 'antd';
import {isEmpty} from 'lodash';
import {initialUpload, notifyBosUpload, renameFileApi, deleteFileApi} from '@/api/upload';
import {messageManager} from '@/utils/errorHandler/MessageManager';
import {handleRequestError} from '@/utils/errorHandler';
import {BosUploader} from '@/utils/bos-sdk';
import {uploadStateStore} from '@/stores/UploadStateStore';

export class AlgorithmStore {
    // 关联的任务 ID
    job_id: string;
    // 初始算法文件树（支持多根节点）
    initial_algorithm: FileInfo[] = [];
    star_initial_algorithm: FileInfo | '' = '';
    // 评估器（从文件树中选择）
    star_evaluator: FileInfo | '' = '';
    selectedFileId: string | null = null;
    editingFile: FileInfo | null = null;
    selectedFileInfo: FileInfo | undefined = undefined;
    isEmpty: boolean = true;
    uploadLoading: boolean = false;
    uploadProgress: number = 0;

    // eslint-disable-next-line @typescript-eslint/prefer-readonly
    private currentBosUploader: BosUploader | null = null;

    constructor(job_id: string = '') {
        this.job_id = job_id;
        makeObservable(this, {
            initial_algorithm: observable,
            uploadLoading: observable,
            uploadProgress: observable,
            star_initial_algorithm: observable,
            star_evaluator: observable,
            editingFile: observable,
            selectedFileId: observable,
            selectedFileInfo: observable,
            isEmpty: observable,
            setAlgorithm: action.bound,
            setJobId: action.bound,
            addNewProject: action.bound,
            getParentNode: action.bound,
            updateFile: action.bound,
            renameFile: flow.bound,
            toggleFileStar: action.bound,
            toggleEvaluatorStar: action.bound,
            clearEvaluatorStar: action.bound,
            clearFileStar: action.bound,
            deleteFile: flow.bound,
            getFile: action.bound,
            reset: action.bound,
            setSelectedFileId: action.bound,
            uploadFileValidate: action.bound,
            setSelectedFile: action.bound,
            selectedFile: computed,
            hasFiles: computed,
            uploadFile: flow.bound
        });
    }

    // 获取选中的文件
    get selectedFile(): FileInfo | null {
        return this.selectedFileId ? this.getFile(this.selectedFileId) : null;
    }

    // 是否有文件
    get hasFiles(): boolean {
        return this.initial_algorithm.length > 0;
    }

    // Algorithm 评估算法为空 或 评估器者星标文件不存在
    get isEmptyStarEvaluatorAlgorithm() {
        if (isEmpty(this.star_evaluator) || isEmpty(this.star_initial_algorithm)
            || !this.star_initial_algorithm || !this.star_evaluator
        ) {
            return true;
        }
    }

    // ====================== 业务方法 ======================
    /**
     * 根据子节点 ID 获取父节点
     * @param childId 子节点 ID
     * @returns 父节点或者 null
     */
    getParentNode(childId: string): FileInfo | null {
        const findParent = (nodes: FileInfo[], childId: string): FileInfo | null => {
            for (const node of nodes) {
                if (node.children?.some(c => c.id === childId)) {
                    return node;
                }
                const parent = node.children ? findParent(node.children, childId) : null;
                if (parent) {
                    return parent;
                }
            }
            return null;
        };
        return findParent(this.initial_algorithm, childId);
    }

    /**
   * 设置当前选中文件
   * @param file FileInfo（包含来源）
   */
    setSelectedFile(file: FileInfo | undefined) {
        this.selectedFileInfo = file;
    }

    setJobId(job_id: string) {
        this.job_id = job_id;
    }
    setEditingFile(file: FileInfo) {
        this.editingFile = file;
    }
    setAlgorithm(initial_algorithm: FileInfo[], star_initial_algorithm: FileInfo, star_evaluator: FileInfo | '' = '') {
        this.initial_algorithm = initial_algorithm;
        this.star_initial_algorithm = star_initial_algorithm;
        this.star_evaluator = star_evaluator || '';

        // 修正选中文件的 ID（文件树重新生成后 ID 会变）
        if (this.selectedFileId && this.selectedFileInfo) {
            const file = this.getFileByPath(this.selectedFileInfo.path);
            this.selectedFileInfo = file;
            this.selectedFileId = file.id;
        }

        // 修正待优化算法的 ID（关键：文件树重新生成后 ID 会变化）
        if (this.star_initial_algorithm) {
            const matched = this.getFileByPath(this.star_initial_algorithm.path);
            if (matched) {
                this.star_initial_algorithm.id = matched.id;
            }
        }

        // 修正评估器的 ID（修复互斥逻辑失效的根本原因）
        if (this.star_evaluator) {
            const matched = this.getFileByPath(this.star_evaluator.path);
            if (matched) {
                // 在文件树中找到了对应文件
                this.star_evaluator = matched;
            }
        }

        this.updateEmptyState();
    }

    // 重命名
    * renameFile(id: string, newName: string, message?: ReturnType<typeof App.useApp>['message']) {
        let root = '';
        if (this.getParentNode(id)?.path) {
            root = this.getParentNode(id)?.path + '/';
        }
        if (root === '' && this.getFile(id)?.is_dir && !this.initial_algorithm?.[0]?.children?.length) {
            this.updateNodeById(id, node => {
                node.name = newName;
                node.path = root + newName;
            });
            return;
        }
        const res: ApiResponse = yield renameFileApi({
            job_id: this.job_id,
            old_name: this.getFile(id)?.path,
            new_name: root + newName,
            tag: 'initial_algorithm',
            is_dir: this.getFile(id)?.is_dir
        });
        if (res && res.code === '0') {
            const oldPath = this.getFile(id)?.path;
            const newPath = root + newName;

            // 同步更新待优化算法的路径
            if (this.star_initial_algorithm?.path === oldPath) {
                this.star_initial_algorithm.path = newPath;
            }

            // 同步更新评估器的路径
            if (this.star_evaluator && this.star_evaluator.path === oldPath) {
                this.star_evaluator.path = newPath;
            }

            // 更新文件树中的节点
            this.updateNodeById(id, node => {
                node.name = newName;
                node.path = newPath;
            });
        } else {
            message?.error(res.message);
        }
    }

    // 更新节点
    updateFile(id: string, newFile: Partial<FileInfo>) {
        this.updateNodeById(id, node => {
            Object.assign(node, newFile);
        });
    }

    // 删除文件/目录
    * deleteFile(id: string, stepStore) {
        // 获取文件（在删除前保存，因为删除后就找不到了）
        const file = this.getFile(id);
        const filePath = file?.path;

        // 判断是否为初始算法目录且只有一个文件（即只有一个元素且没有子节点）
        // 判断是否需要跳过删除操作
        if (this.shouldSkipDeletion(file, id)) {
            // 从初始算法中移除文件
            this.removeFileFromInitialAlgorithm(id);
            this.cleanupAfterDeletion(id, filePath, stepStore);
            return;
        }

        // 调用删除文件的API
        const normalizedFilePath = filePath && !filePath.startsWith('/') && !filePath.includes('/')
            ? `/${filePath}`
            : filePath;
        const res: ApiResponse = yield deleteFileApi({
            job_id: this.job_id,
            file_name: normalizedFilePath,
            is_dir: file?.is_dir,
            tag: 'initial_algorithm'
        });

        // 判断API返回的结果
        if (res?.code === '0') {
            // 从初始算法中移除文件
            this.removeFileFromInitialAlgorithm(id);
            this.cleanupAfterDeletion(id, filePath, stepStore);
        } else {
            messageManager.message.error(res.message);
        }
    }


    // 星标（待优化算法）
    toggleFileStar(id: string, stepStore: any, callback) {
        const file = this.getFile(id);
        if (!file) {
            return;
        }

        // 互斥逻辑：如果该文件已被选为评估器，清空评估器
        // 注意：使用 path 比较而不是 id，因为 id 会在文件树重新生成时变化
        if (this.star_evaluator && this.star_evaluator.id === file.id) {
            this.star_evaluator = '';
        }

        // 清空所有文件的星标
        for (const root of this.initial_algorithm) {
            this.clearAllStars(root);
        }

        // 设置新的待优化算法
        this.star_initial_algorithm = file;
        this.updateFile(id, {star: true});

        // 回调更新：同时传递待优化算法和评估器（如果被清空）
        callback({
            star_initial_algorithm: this.normalizeRootPath(this.star_initial_algorithm.path),
            star_evaluator: this.star_evaluator ? this.normalizeRootPath(this.star_evaluator.path) : ''
        });
    }

    // 设置评估器
    toggleEvaluatorStar(id: string, stepStore: any, callback: any) {
        const file = this.getFile(id);
        if (!file) {
            return;
        }

        // 如果点击的是已设为评估器的文件 -> 不需要处理
        if (this.star_evaluator && this.star_evaluator.id === file.id) {
            return;
        }

        // 互斥逻辑：如果该文件已被选为待优化算法，清空待优化算法
        if (file.star) {
            // 清空所有文件的星标
            for (const root of this.initial_algorithm) {
                this.clearAllStars(root);
            }
            this.star_initial_algorithm = '';
        }

        // 设置新的评估器
        this.star_evaluator = file;

        // 回调更新：同时传递评估器和待优化算法（如果被清空）
        callback({
            star_evaluator: this.normalizeRootPath(this.star_evaluator.path),
            star_initial_algorithm: this.star_initial_algorithm
                ? this.normalizeRootPath(this.star_initial_algorithm.path)
                : ''
        });
    }

    // 清除评估器
    clearEvaluatorStar(callback?: any) {
        this.star_evaluator = '';
        if (callback) {
            callback({
                star_evaluator: ''
            });
        }
    }

    // 清除待优化算法
    clearFileStar(callback?: any) {
        if (this.star_initial_algorithm) {
            // 清掉文件树中的 star 标记
            for (const root of this.initial_algorithm) {
                this.clearAllStars(root);
            }
            this.star_initial_algorithm = '';
            if (callback) {
                callback({
                    star_initial_algorithm: ''
                });
            }
        }
    }

    // 获取文件节点
    getFile(id: string): FileInfo | undefined {
        for (const root of this.initial_algorithm) {
            const found = this.findNodeById(root, id);
            if (found) {
                this.selectedFileInfo = found;
                return found;
            }
        }
        return undefined;
    }
    getFileByPath(path: string): FileInfo | undefined {
        for (const root of this.initial_algorithm) {
            const found = this.findNodeByPath(root, path);
            if (found) {
                return found;
            }
        }
        return undefined;
    }

    // 重置
    reset() {
        this.initial_algorithm = [];
        this.star_initial_algorithm = '';
        this.star_evaluator = '';
        this.setSelectedFileId(null);
        this.updateEmptyState();
    }

    // 设置选中的文件
    setSelectedFileId(id: string | null, source: 'origin' | 'optimized' = 'origin') {
        this.selectedFileId = id;
        if (id) {
            const file = this.getFile(id);
            if (file) {
                this.selectedFileInfo = {
                    ...file,
                    source // 给 file 打上来源标记
                };
            } else {
                this.selectedFileInfo = undefined;
            }
        } else {
            this.selectedFileInfo = undefined;
        }
    }

    // 新建工程
    addNewProject(name: string) {
        this.initial_algorithm.push(
            {
                id: uuidv4(),
                name: name,
                is_dir: true,
                path: name,
                bos_url: '',
                star: false,
                children: []
            }
        );
        this.setEditingFile(this.initial_algorithm[0]);
        this.updateEmptyState();
    }

    // 上传文件
    // useBos: true 走 BOS 上传（上传文件夹），false 直接调用 initialUpload（内容编辑/新建文件）
    * uploadFile(uploadParams, useBos: boolean = false) {
        try {
            this.uploadLoading = true;
            this.uploadProgress = 0;

            const {job_id, path, cover_dir} = uploadParams;
            const files: File[] = uploadParams.initial_algorithm;

            const res: ApiResponse = useBos
                ? yield this.uploadViaBos(job_id, path, cover_dir, files)
                : yield this.uploadViaApi(job_id, path, cover_dir, files);

            if (res && res.code === '0') {
                this.updateEmptyState();
                if (cover_dir) {
                    this.star_initial_algorithm = '';
                }
            } else {
                console.error(res);
                handleRequestError({error: res});
            }
        } catch (error: unknown) {
            if (error instanceof Error && error.message === 'upload aborted') {
                return;
            }
            handleRequestError({error});
        } finally {
            this.uploadLoading = false;
            this.uploadProgress = 0;
            uploadStateStore.finishUpload();
        }
    }

    // 上传验证
    uploadFileValidate(): boolean {
        return this.hasFiles;
    }

    private async uploadViaBos(
        job_id: string, path: string, cover_dir: boolean, files: File[]
    ): Promise<ApiResponse> {
        const totalFiles = files.length;
        let processedCount = 0;
        const bosUploader = new BosUploader(job_id);
        uploadStateStore.startUpload(bosUploader);
        const bosFiles: BosFiles = [];
        const failedFiles: string[] = [];

        for (const file of files) {
            // 单/多文件上传时 path 由 parent_path 表达，bosFiles.path 仅保留相对文件路径
            const relativePath = (file as any).webkitRelativePath || file.name;
            const normalizedPath = path
                ? path.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '')
                : '';
            const normalizedRelativePath = relativePath.replace(/\\/g, '/').replace(/^\/+/, '');
            const bosKey = `fm-console/${job_id}/initial_algorithm/${normalizedPath ? `${normalizedPath}/` : ''}${normalizedRelativePath}`;

            try {
                const currentCount = processedCount;
                const url: string = await bosUploader.multipartUpload(bosKey, file, (partPercent: number) => {
                    const overall = ((currentCount + partPercent / 100) / totalFiles) * 100;
                    this.uploadProgress = Math.round(overall);
                });

                bosFiles.push({file_name: file.name, bos_url: url, file_size: file.size, path: relativePath});
            } catch (error: any) {
                const statusCode = Number(error?.status_code || error?.statusCode || error?.response?.status || 0);
                if (statusCode !== 400) {
                    throw error;
                }
                failedFiles.push(relativePath);
                console.warn(`文件上传返回 400，已跳过: ${relativePath}`, error);
            } finally {
                processedCount++;
                this.uploadProgress = Math.round((processedCount / totalFiles) * 100);
            }
        }

        if (!bosFiles.length) {
            throw new Error('所有文件上传失败');
        }

        if (failedFiles.length) {
            messageManager.message.warning(`部分文件上传失败（400），已跳过 ${failedFiles.length} 个文件`);
        }

        const parentPath = cover_dir ? (path || '') : (`${path}/` || '/');

        // 单/多文件上传时，先删除旧同名文件，再通知后端
        if (!cover_dir) {
            const normalizedParent = (path || '').replace(/^\/+|\/+$/g, '');
            for (const item of bosFiles) {
                const fullPath = normalizedParent ? `${normalizedParent}/${item.path}` : item.path;
                const existedFile = this.getFileByPath(fullPath);
                if (!existedFile || existedFile.is_dir) {
                    continue;
                }
                const deleteRes: ApiResponse = await deleteFileApi({
                    job_id,
                    file_name: fullPath,
                    is_dir: false,
                    tag: 'initial_algorithm'
                });
                if (deleteRes?.code !== '0') {
                    messageManager.message.warning(`覆盖旧文件失败: ${fullPath}`);
                }
            }
        }

        return notifyBosUpload({
            job_id,
            parent_path: parentPath,
            cover_dir: !!cover_dir,
            initial_algorithm: bosFiles
        });
    }

    private async uploadViaApi(
        job_id: string, path: string, cover_dir: boolean, files: File[]
    ): Promise<ApiResponse> {
        const formData = new FormData();
        formData.append('job_id', job_id);
        formData.append('path', path);
        formData.append('cover_dir', String(cover_dir));
        files.forEach((file: File) => {
            formData.append('initial_algorithm', file);
        });
        return initialUpload(formData, job_id);
    }

    // ====================== 树操作工具函数 ======================

    private findNodeById(node: FileInfo, id: string): FileInfo | null {
        if (node.id === id) {
            return node;
        }
        if (node.children) {
            for (const child of node.children) {
                const result = this.findNodeById(child, id);
                if (result) {
                    return result;
                }
            }
        }
        return null;
    }

    private findNodeByPath(node: FileInfo, path: string): FileInfo | null {
        if (node.path === path) {
            return node;
        }
        if (node.children) {
            for (const child of node.children) {
                const result = this.findNodeByPath(child, path);
                if (result) {
                    return result;
                }
            }
        }
        return null;
    }

    private updateNodeById(id: string, updater: (node: FileInfo) => void): boolean {
        for (const root of this.initial_algorithm) {
            if (this._updateNode(root, id, updater)) {
                return true;
            }
        }
        return false;
    }

    private _updateNode(node: FileInfo, id: string, updater: (node: FileInfo) => void): boolean {
        if (node.id === id) {
            updater(node);
            return true;
        }
        if (node.children) {
            for (const child of node.children) {
                if (this._updateNode(child, id, updater)) {
                    return true;
                }
            }
        }
        return false;
    }

    private deleteNodeById(node: FileInfo, id: string): boolean {
        if (!node.children) {
            return false;
        }
        const index = node.children.findIndex(c => c.id === id);
        if (index !== -1) {
            node.children.splice(index, 1);
            return true;
        }
        for (const child of node.children) {
            if (this.deleteNodeById(child, id)) {
                return true;
            }
        }
        return false;
    }

    // 更新空状态
    private updateEmptyState() {
        this.isEmpty = !this.hasFiles;
    }

    // 递归清掉所有 star
    private clearAllStars(node: FileInfo) {
        if (node.star) {
            node.star = false;
        }
        if (node.children) {
            node.children.forEach(child => this.clearAllStars(child));
        }
    }

    // 判断是否跳过删除操作的条件
    private shouldSkipDeletion(file: File | undefined, id: string): boolean {
        return file?.is_dir && this.initial_algorithm.length === 1
        && this.initial_algorithm[0].id === id && this.initial_algorithm[0].children.length === 0;
    }

    // 根目录平级文件路径补上前导 '/'（与 deleteFile 的 normalizedFilePath 规则保持一致）
    private normalizeRootPath(path: string): string {
        if (!path) {
            return path;
        }
        return !path.startsWith('/') && !path.includes('/') ? `/${path}` : path;
    }

    // 从 initial_algorithm 中移除文件
    private removeFileFromInitialAlgorithm(id: string): void {
        const index = this.initial_algorithm.findIndex(n => n.id === id);

        if (index === -1) {
            // 在嵌套的children中查找并删除
            this.initial_algorithm.forEach(root => this.deleteNodeById(root, id));
            // 强制触发MobX更新：创建新的数组引用
            this.initial_algorithm = [...this.initial_algorithm];
        } else {
            // 在根节点找到，直接删除
            this.initial_algorithm.splice(index, 1);
        }
    }

    // 更新 star_initial_algorithm 状态
    private updateStarInitialAlgorithm(stepStore): void {
        if (this.selectedFileInfo?.path === this.star_initial_algorithm?.path || !this.initial_algorithm.length) {
            this.star_initial_algorithm = '';
            stepStore.markCompleted(0, false);
        }
    }

    // 如果当前选中的文件是要删除的文件，重置选中的文件
    private resetSelectedFileIfNeeded(id: string): void {
        if (this.selectedFileId === id) {
            this.setSelectedFileId(null);
        }
    }

    // 删除后的统一清理操作
    private cleanupAfterDeletion(id: string, filePath: string | undefined, stepStore: any): void {
        // 清空待优化算法（如果被删除的是待优化算法）
        this.updateStarInitialAlgorithm(stepStore);

        // 清空评估器（如果被删除的是评估器）
        if (filePath && this.star_evaluator && this.star_evaluator.path === filePath) {
            this.star_evaluator = '';
        }

        this.resetSelectedFileIfNeeded(id);
        this.updateEmptyState();
    }
}
